import React, { createContext, useContext, useRef } from 'react';
import { createStore, useStore } from 'zustand';
import {
  CoordsUtils,
  clamp,
  incrementZoom,
  decrementZoom,
  getStartingMode
} from 'src/utils';
import { UiStateStore } from 'src/types';
import { INITIAL_UI_STATE, MAX_ZOOM, MIN_ZOOM } from 'src/config';

const initialState = () => {
  return createStore<UiStateStore>((set, get) => {
    return {
      zoom: INITIAL_UI_STATE.zoom,
      scroll: INITIAL_UI_STATE.scroll,
      view: '',
      mainMenuOptions: [],
      editorMode: 'EDITABLE', // ← Changed from 'EXPLORABLE_READONLY'
      mode: getStartingMode('EDITABLE'), // ← Changed from 'EXPLORABLE_READONLY'
      iconCategoriesState: [],
      isMainMenuOpen: false,
      dialog: null,
      rendererEl: null,
      contextMenu: null,
      mouse: {
        position: { screen: CoordsUtils.zero(), tile: CoordsUtils.zero() },
        mousedown: null,
        delta: null
      },
      itemControls: null,
      enableDebugTools: false,
      showExportDialog: false,
      dropTargetGroupId: null,
      showGrid: true,
      selectionCycle: null,
      editingTextBoxId: null,
      editingModelItemId: null,
      smartGuides: [],
      actions: {
        setView: (view) => {
          set({ view });
        },
        setMainMenuOptions: (mainMenuOptions) => {
          set({ mainMenuOptions });
        },
        setEditorMode: (mode) => {
          set({ editorMode: mode, mode: getStartingMode(mode) });
        },
        setIconCategoriesState: (iconCategoriesState) => {
          set({ iconCategoriesState });
        },
        resetUiState: () => {
          set({
            mode: getStartingMode(get().editorMode),
            scroll: {
              position: CoordsUtils.zero(),
              offset: CoordsUtils.zero()
            },
            itemControls: null,
            selectionCycle: null,
            editingTextBoxId: null,
            editingModelItemId: null,
            smartGuides: [],
            zoom: 1
          });
        },
        setMode: (mode) => {
          set({ mode });
        },
        setDialog: (dialog) => {
          set({ dialog });
        },
        setIsMainMenuOpen: (isMainMenuOpen) => {
          set({ isMainMenuOpen, itemControls: null });
        },
        incrementZoom: () => {
          const { zoom } = get();
          set({ zoom: incrementZoom(zoom) });
        },
        decrementZoom: () => {
          const { zoom } = get();
          set({ zoom: decrementZoom(zoom) });
        },
        setZoom: (zoom) => {
          set({ zoom: clamp(zoom, MIN_ZOOM, MAX_ZOOM) });
        },
        setScroll: ({ position, offset }) => {
          set({ scroll: { position, offset: offset ?? get().scroll.offset } });
        },
        setItemControls: (itemControls) => {
          set({ itemControls });
        },
        setContextMenu: (contextMenu) => {
          set({ contextMenu });
        },
        setMouse: (mouse) => {
          set({ mouse });
        },
        setEnableDebugTools: (enableDebugTools) => {
          set({ enableDebugTools });
        },
        setRendererEl: (el) => {
          set({ rendererEl: el });
        },
        openExportDialog: () => {
          set({ showExportDialog: true });
        },
        closeExportDialog: () => {
          set({ showExportDialog: false });
        },
        setDropTargetGroupId: (groupId) => {
          set({ dropTargetGroupId: groupId });
        },
        setShowGrid: (show) => {
          set({ showGrid: show });
        },
        setSelectionCycle: (selectionCycle) => {
          set({ selectionCycle });
        },
        setEditingTextBoxId: (editingTextBoxId) => {
          set({ editingTextBoxId });
        },
        setEditingModelItemId: (editingModelItemId) => {
          set({ editingModelItemId });
        },
        setSmartGuides: (smartGuides) => {
          set({ smartGuides });
        }
      }
    };
  });
};

const UiStateContext = createContext<ReturnType<typeof initialState> | null>(
  null
);

interface ProviderProps {
  children: React.ReactNode;
}

/**
 * UiStateProvider: Provides Zustand store via React Context
 *
 * This pattern allows multiple independent store instances in the component tree
 * and prevents state sharing between different diagram instances. Each provider
 * creates its own store instance, enabling features like opening multiple diagrams
 * in different tabs/components.
 *
 * Reference: https://github.com/pmndrs/zustand/discussions/1180#discussioncomment-3439061
 */
export const UiStateProvider = ({ children }: ProviderProps) => {
  const storeRef = useRef<ReturnType<typeof initialState>>();

  if (!storeRef.current) {
    storeRef.current = initialState();
  }

  return (
    <UiStateContext.Provider value={storeRef.current}>
      {children}
    </UiStateContext.Provider>
  );
};

export function useUiStateStore<T>(selector: (state: UiStateStore) => T) {
  const store = useContext(UiStateContext);

  if (store === null) {
    throw new Error('Missing provider in the tree');
  }

  const value = useStore(store, selector);
  return value;
}
